import type { CreateOrderDTO, CreateOrderDTOLine } from "./dto";
import { isDraftRestorable, type DraftScope } from "./is-draft-restorable";

const DRAFT_STORAGE_KEY = "order-draft";

export type DraftScopeInput = Pick<DraftScope, "userId" | "companyId">;

/** The composed order, minus `docNumber` — that is regenerated per session. */
export type OrderDraft = Pick<CreateOrderDTO, "lines" | "note" | "POOrderNumber">;

interface StoredDraft extends OrderDraft, DraftScope {}

const isCompleteScope = (scope: DraftScopeInput): boolean =>
  scope.userId !== null && scope.companyId !== null;

/**
 * Returns the stored draft when it belongs to `scope` and has not gone stale,
 * and deletes it otherwise so a rejected draft never lingers.
 *
 * An incomplete scope is not a rejection: a superuser has no active company
 * until they pick one, so nothing can be decided yet and the draft is left be.
 */
export const readOrderDraft = (scope: DraftScopeInput): OrderDraft | null => {
  if (!isCompleteScope(scope)) return null;

  let stored: StoredDraft | null = null;

  try {
    const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
    stored = raw ? (JSON.parse(raw) as StoredDraft) : null;
  } catch {
    clearOrderDraft();
    return null;
  }

  if (!stored) return null;

  if (!isDraftRestorable({ persisted: stored, current: scope, now: Date.now() })) {
    clearOrderDraft();
    return null;
  }

  return {
    lines: Array.isArray(stored.lines) ? stored.lines : [],
    note: stored.note ?? "",
    POOrderNumber: stored.POOrderNumber ?? "",
  };
};

/**
 * Writes the draft, stamping `savedAt` so the expiry window measures time since
 * the last edit. An empty order is deleted rather than stored.
 */
export const writeOrderDraft = (
  draft: OrderDraft,
  scope: DraftScopeInput,
): void => {
  if (!isCompleteScope(scope)) return;

  if (!draft.lines.length && !draft.note && !draft.POOrderNumber) {
    clearOrderDraft();
    return;
  }

  const payload: StoredDraft = { ...draft, ...scope, savedAt: Date.now() };

  try {
    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* Storage full or blocked — the draft is a convenience, not a guarantee. */
  }
};

export const clearOrderDraft = (): void => {
  try {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
  } catch {
    /* Nothing to do if storage is unavailable. */
  }
};

export const hasRestorableItemLines = (lines: CreateOrderDTOLine[]): boolean =>
  lines.some((line) => !!line.item);
